import React from 'react'

import { Box, Icon, Text, dimensions } from '@factorialco/deprecated-design-system'
import { ExtraHoursRangeFragment } from 'generated/resources/graphql'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { getCompensationMethod } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/HoursRules/utils/getCompensationsMethod'
import { convertBackendMinutesToHours } from 'modules/attendance/components/SettingItemPage/TimeTracking/WorkTimeCategories/Content/utils/extraHoursUtils'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { EXTRA_HOURS_RANGE_BASELINE } from 'modules/attendance/lib/enums'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import ExtraHoursRangeUtils from 'modules/attendance/lib/utils/ExtraHoursRangeUtils'

interface RuleDetailsProps {
  rule: ExtraHoursRangeFragment
}

const RuleDetails = ({ rule }: RuleDetailsProps) => {
  const { hasContractHoursBaseline, extraHoursRanges, complementaryHoursRanges } =
    useAttendanceSettingWorkCategories()
  const { fromHour, toHour } = convertBackendMinutesToHours(rule.fromMinute, rule.toMinute)

  const isComplementaryRule = rule.employeeType === EMPLOYEE_TYPE.PART_TIME
  const relevantRanges = isComplementaryRule ? complementaryHoursRanges : extraHoursRanges

  const anyRuleFixed = relevantRanges?.some(
    (r) => r.baseline === EXTRA_HOURS_RANGE_BASELINE.contract_hours
  )
  const showApplyBasedOnRow =
    hasContractHoursBaseline ||
    anyRuleFixed ||
    rule.baseline === EXTRA_HOURS_RANGE_BASELINE.contract_hours

  const isDefaultRule = ExtraHoursRangeUtils.isDefault(rule)

  const defaultTrackingCopy = i18n.t(
    'time_tracking.settings.work_time_categories.extra_hours_rules.base_rule.all_hours_beyond_reference_point'
  )

  const trackingConditionValue = isDefaultRule ? (
    <Text size="300" title={defaultTrackingCopy}>
      {defaultTrackingCopy}
    </Text>
  ) : (
    <Box flexDirection="row" gap="s4" flexWrap="wrap" style={{ rowGap: '4px' }}>
      <Text color="grey1000" size="300" weight="medium">
        {i18n.t('time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.from')}
      </Text>
      <Text size="300">
        {i18n.t('time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.hours', {
          count: fromHour,
        })}
      </Text>
      <Text color="grey1000" size="300" weight="medium">
        {i18n.t('time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.to')}
      </Text>
      <Text size="300">
        {rule.toMinute && !rule.unlimited
          ? i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.hours',
              { count: toHour }
            )
          : i18n.t(
              'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.unlimited'
            )}
      </Text>
    </Box>
  )

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        columnGap: `${dimensions.s16}`,
        rowGap: `${dimensions.s12}`,
      }}
    >
      <Box flexDirection="row" alignItems="flexStart" gap="s8">
        <Icon.Medium icon="clock" color="grey800" />
        <Text color="grey1000" size="300" weight="medium">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.tracking_condition'
          )}
        </Text>
      </Box>
      <Box flexDirection="column" style={{ minWidth: '0' }}>
        {trackingConditionValue}
      </Box>

      {showApplyBasedOnRow && (
        <>
          <Box flexDirection="row" alignItems="flexStart" gap="s8">
            <Icon.Medium icon="arrow-right" color="grey800" />
            <Text color="grey1000" size="300" weight="medium">
              {i18n.t(
                'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.apply_based_on'
              )}
            </Text>
          </Box>
          <Text.Normal size="300">
            {rule.baseline === EXTRA_HOURS_RANGE_BASELINE.contract_hours
              ? i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.fixed_hours'
                )
              : i18n.t(
                  'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.effective_hours'
                )}
          </Text.Normal>
        </>
      )}

      <Box flexDirection="row" alignItems="flexStart" gap="s8">
        <Icon.Medium icon="calculator" color="grey800" />
        <Text color="grey1000" size="300" weight="medium">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.hourly_rate'
          )}
        </Text>
      </Box>
      <Box flexDirection="row" gap="s4">
        <Text.Normal size="300">{minutesToText(rule.rawMinutesInCents / 100)}</Text.Normal>
        <Text.Normal size="300">{'->'}</Text.Normal>
        <Text.Normal size="300">{minutesToText(rule.equivalentMinutesInCents / 100)}</Text.Normal>
      </Box>

      <Box flexDirection="row" alignItems="flexStart" gap="s8">
        <Icon.Medium icon="money-bag" color="grey800" />
        <Text color="grey1000" size="300" weight="medium">
          {i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.custom_rule.compensation_method'
          )}
        </Text>
      </Box>
      <Text.Normal size="300">{getCompensationMethod(rule.poolType)}</Text.Normal>
    </Box>
  )
}

export default RuleDetails
